import merge from 'lodash/merge';
import { DateTime } from 'luxon';
import { put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { generatedReportsActions } from 'app-components/flux/generated-reports';
import type { generatedReportsTypes } from 'app-components/flux/generated-reports';
import { reportConfigurationsTypes } from 'app-components/flux/report-configurations';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsTypes } from 'app-components/flux/transactions';

import { resourceSagaUtils, resourceStoreTypes } from '../core';

import type { CoreActionCreator } from '../core/resourceStoreTypes';
import * as actions from './taxesActions';
import * as taxesSelectors from './taxesSelectors';
import {
  transformServerResourceToClientResource,
  transformTaxInputClientResourceToRequestData,
  transformTaxInputServerResourceToClientResource,
} from './taxesTransformers';
import * as taxesTypes from './taxesTypes';
import * as taxesUtils from './taxesUtils';

const mkQcsSyncTaxInput = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<taxesTypes.TaxInputProps>();
const mkTaxInputResourceConfig = (
  successAction: CoreActionCreator<any, any>,
  failureAction: CoreActionCreator<any, any>,
) =>
  mkQcsSyncTaxInput({
    resourceName: 'taxes',
    resourceBaseUrl: '/taxes/tax-inputs',
    getLastSyncDate: taxesSelectors.getLastSyncDate,

    transformClientResourceToRequestData: transformTaxInputClientResourceToRequestData,
    transformServerResourceToClientResource: transformTaxInputServerResourceToClientResource,

    successAction,
    failureAction,
  });

const mkQcsSyncTaxConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<taxesTypes.TaxProps>();
const mkTaxResourceConfig = (successAction: CoreActionCreator<any, any>, failureAction: CoreActionCreator<any, any>) =>
  mkQcsSyncTaxConfig({
    resourceName: 'taxes',
    resourceBaseUrl: '/taxes/tax-calculations',
    getLastSyncDate: taxesSelectors.getLastSyncDate,

    transformServerResourceToClientResource,

    successAction,
    failureAction,
  });

export function* getTaxCalculationsActionWatcher() {
  const resourceConfig = mkTaxResourceConfig(actions.getTaxCalculationsSuccess, actions.getTaxCalculationFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getTaxCalculations.type,
    resourceSagaUtils.qcsSyncGetResources<taxesTypes.TaxProps>,
    resourceConfig,
  );
}

export function* getTaxInputsActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.getTaxInputsSuccess, actions.getTaxInputsFailure);
  yield takeEvery(
    actions.getTaxInputs.type,
    resourceSagaUtils.qcsSyncGetResources<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* createTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.createTaxInputSuccess, actions.createTaxInputFailure);
  yield takeEvery(
    actions.createTaxInput.type,
    resourceSagaUtils.qcsSyncCreateResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* updateTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.updateTaxInputSuccess, actions.updateTaxInputFailure);
  yield takeEvery(
    actions.updateTaxInput.type,
    resourceSagaUtils.qcsSyncUpdateResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* deleteTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.deleteTaxInputSuccess, actions.deleteTaxInputFailure);
  yield takeEvery(
    actions.deleteTaxInput.type,
    resourceSagaUtils.qcsSyncDeleteResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* createTaxInputFailureActionWatcher() {
  yield takeEvery([actions.createTaxInputFailure.type, actions.updateTaxInputFailure.type], actions.getTaxCalculations);
}

export function* performTaxCalcActionWatcher() {
  yield takeEvery(actions.performTaxCalc.type, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: (action) => `/taxes/tax-calculations/calc/${action.payload}`,
    successAction: actions.performTaxCalcSuccess,
    failureAction: actions.performTaxCalcFailure,
  });
}

const getValidNumber = (value?: number) => (typeof value === 'number' && isFinite(value) ? value : undefined);

export function* performTaxPlanRefreshActionWatcher() {
  yield takeLeading(
    actions.performTaxPlanRefresh.type,
    function* performTaxPlanRefresh(
      action: ReturnType<typeof actions.performTaxPlanRefresh>,
    ): Generator<any, any, any> {
      try {
        if (!action.payload.id && (!action.payload.year || !action.meta?.props.recurrenceAccountId)) {
          yield put(actions.performTaxPlanRefreshFailure(new Error('Missing parameters')));
          return { state: 'MISSING_PARAMS_ERROR' };
        }

        const taxPlan: taxesTypes.Tax | undefined = yield select(
          taxesSelectors.getTaxPlanById,
          action.payload.id || '',
        );
        const year = action.payload.year || taxPlan?.input?.year;
        if (year) {
          // generate tax report for the year
          const startOn = DateTime.fromObject({ year, month: 1, day: 1 }).toISODate() || undefined;
          const endOn = DateTime.fromObject({ year, month: 12, day: 31 }).toISODate() || undefined;
          const reportConfig = reportConfigurationsTypes.mkReportConfiguration({
            name: 'taxes-plan',
            accountingType: 'CASH',
            reportType: 'TRANSACTION',
            reportSubType: 'TAX_SUMMARY',
            persistentType: 'TEMPORARY',
            dateRange: 'CUSTOM',
            startOn,
            endOn,
            columnType: 'NONE',
            rowType: 'USAGE',
          });
          yield put(
            generatedReportsActions.generateReport({
              location: 'tax-planner-tax-summary',
              configuration: reportConfig,
            }),
          );
          const { generateReportSuccess } = yield race({
            generateReportSuccess: take(generatedReportsActions.generateReportSuccess),
            generateReportFailure: take(generatedReportsActions.generateReportFailure),
          });
          if (!generateReportSuccess) {
            assert(false, 'tax planner: generate report error', generateReportSuccess?.payload);
            action.meta?.reject?.({ state: 'TAX_REPORT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax report generation failed')));
            return { state: 'TAX_REPORT_ERROR' };
          }

          // apply transactions and overrides on top of the tax report
          let taxInput;
          try {
            const taxQuarters = yield select(taxesSelectors.getTaxQuarters, action.payload.id);
            const taxInputPropsResult = taxesUtils.mapTaxReportToTaxInput(
              generateReportSuccess.payload.report as generatedReportsTypes.Report,
            );
            merge(taxInputPropsResult, {
              ...(!action.payload.id && { clientId: uuidv4().toUpperCase() }),
              taxPayments: {
                estimatedTaxesPaidOrPlannedApril: taxQuarters.get(0)?.amountPaid,
                estimatedTaxesPaidOrPlannedJune: taxQuarters.get(1)?.amountPaid,
                estimatedTaxesPaidOrPlannedSeptember: taxQuarters.get(2)?.amountPaid,
                estimatedTaxesPaidOrPlannedJanuary: taxQuarters.get(3)?.amountPaid,
              },
              year,
              filingStatus: taxPlan?.input?.filingStatus,
              ...action.payload,
              // TODO: figure out better mechanism to override report values with user input
              wages: {
                selfWages:
                  getValidNumber(taxPlan?.input?.wages?.selfWages) || taxInputPropsResult.wages?.selfWages || 0,
                spouseWages:
                  getValidNumber(taxPlan?.input?.wages?.spouseWages) || taxInputPropsResult.wages?.spouseWages || 0,
                ...action.payload.wages,
              },
              profile: {
                numberOfDependents: taxPlan?.input?.profile?.numberOfDependents,
                ...action.payload.profile,
              },
              withholdingsAndSummary: {
                withholdingsSelf:
                  getValidNumber(taxPlan?.input?.withholdingsAndSummary?.withholdingsSelf) ||
                  taxInputPropsResult.withholdingsAndSummary?.withholdingsSelf ||
                  0,
                withholdingsSpouse:
                  getValidNumber(taxPlan?.input?.withholdingsAndSummary?.withholdingsSpouse) ||
                  taxInputPropsResult.withholdingsAndSummary?.withholdingsSpouse ||
                  0,
                ...action.payload.withholdingsAndSummary,
              },
              divInterest: {
                taxableInterest:
                  getValidNumber(taxPlan?.input?.divInterest?.taxableInterest) ||
                  taxInputPropsResult.divInterest?.taxableInterest ||
                  0,
                dividends:
                  getValidNumber(taxPlan?.input?.divInterest?.dividends) ||
                  taxInputPropsResult.divInterest?.dividends ||
                  0,
                qualifiedDividends:
                  getValidNumber(taxPlan?.input?.divInterest?.qualifiedDividends) ||
                  taxInputPropsResult.divInterest?.qualifiedDividends ||
                  0,
                ...action.payload.divInterest,
              },
              capitalGains: {
                shortTermGains:
                  getValidNumber(taxPlan?.input?.capitalGains?.shortTermGains) ||
                  taxInputPropsResult.capitalGains?.shortTermGains ||
                  0,
                longTermGains:
                  getValidNumber(taxPlan?.input?.capitalGains?.longTermGains) ||
                  taxInputPropsResult.capitalGains?.longTermGains ||
                  0,
                ...action.payload.capitalGains,
              },
              otherIncome: {
                otherIncomeGainsOrLosses:
                  getValidNumber(taxPlan?.input?.otherIncome?.otherIncomeGainsOrLosses) ||
                  taxInputPropsResult.otherIncome?.otherIncomeGainsOrLosses ||
                  0,
                ...action.payload.otherIncome,
              },
            });

            taxInput = taxesTypes.mkTaxInput(taxInputPropsResult);
          } catch (error) {
            assert(false, error);
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input transformation failed')));
            return { state: 'TAX_INPUT_ERROR' };
          }
          if (!taxInput) {
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input is empty')));
            return { state: 'TAX_INPUT_ERROR' };
          }

          // update/create tax input
          let taxPlanId;
          if (action.payload.id) {
            yield put(actions.updateTaxInput(taxInput, { axiosConfig: { params: { doReplace: true } } }));
            const { updateTaxInputSuccess } = yield race({
              updateTaxInputSuccess: take(actions.updateTaxInputSuccess),
              updateTaxInputFailure: take(actions.updateTaxInputFailure),
            });
            taxPlanId = updateTaxInputSuccess.payload.id;
          } else {
            yield put(actions.createTaxInput(taxInput));
            const { createTaxInputSuccess } = yield race({
              createTaxInputSuccess: take(actions.createTaxInputSuccess),
              createTaxInputFailure: take(actions.createTaxInputFailure),
            });
            taxPlanId = createTaxInputSuccess.payload.id;
          }
          if (!taxPlanId) {
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input creation failed')));
            return { state: 'TAX_INPUT_ERROR' };
          }

          // calculate taxes
          yield put(actions.performTaxCalc(taxPlanId));
          const { performTaxCalcSuccess } = yield race({
            performTaxCalcSuccess: take(actions.performTaxCalcSuccess),
            performTaxCalcFailure: take(actions.performTaxCalcFailure),
          });
          if (!performTaxCalcSuccess.payload.data?.resource?.output?.withholdingsAndSummaryCalc) {
            assert(false, 'tax calc error', performTaxCalcSuccess.payload);
            action.meta?.reject?.({ state: 'CALCULATION_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax calculation failed')));
            return { state: 'CALCULATION_ERROR' };
          }

          // create/update recurring based on WithholdingsAndSummary_CALC_RemainingTaxDue
          // TODO: make it smarter and update instances of the model
          const taxPlanStats = yield select(taxesSelectors.getTaxPlanStats, taxPlanId);
          if (action.payload.id && taxPlan?.input?.profile?.stModelId) {
            // update
            const upcomintgTransactionsCount = taxPlanStats.transactionsUpcoming.length;
            if (upcomintgTransactionsCount) {
              const taxQuarterAmount =
                -performTaxCalcSuccess.payload.data.resource.output.withholdingsAndSummaryCalc.remainingTaxDue /
                upcomintgTransactionsCount;
              const existingScheduledTransaction = yield select(
                scheduledTransactionsSelectors.getScheduledTransactionById,
                taxPlan.input.profile.stModelId,
              );
              if (existingScheduledTransaction.transaction.amount !== taxQuarterAmount) {
                yield put(
                  scheduledTransactionsActions.updateScheduledTransaction(
                    scheduledTransactionsTypes.mkScheduledTransaction({
                      id: performTaxCalcSuccess.payload.data.resource.input.profile.stModelId,
                      transaction: transactionsTypes.mkCashFlowTransactionPatch({
                        amount: taxQuarterAmount,
                      }),
                    }),
                  ),
                );
                const { updateScheduledTransactionSuccess } = yield race({
                  updateScheduledTransactionSuccess: take(
                    scheduledTransactionsActions.updateScheduledTransactionSuccess,
                  ),
                  updateScheduledTransactionFailure: take(
                    scheduledTransactionsActions.updateScheduledTransactionFailure,
                  ),
                });
                if (!updateScheduledTransactionSuccess) {
                  action.meta?.reject?.({ state: 'SCHEDULED_TRANSACTION_UPDATE_ERROR' });
                  yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input update failed')));
                  return { state: 'SCHEDULED_TRANSACTION_UPDATE_ERROR' };
                }
              }
            }
          } else {
            // create
            if (!action.meta?.props.recurrenceAccountId) {
              action.meta?.reject?.({ state: 'MISSING_ACCOUNT_ERROR' });
              yield put(actions.performTaxPlanRefreshFailure(new Error('Missing recurrence account')));
              return { state: 'MISSING_ACCOUNT_ERROR' };
            }
            const upcomintgTransactionsCount = 4;
            const taxQuarterAmount =
              -performTaxCalcSuccess.payload.data.resource.output.withholdingsAndSummaryCalc.remainingTaxDue /
              upcomintgTransactionsCount;

            yield put(
              scheduledTransactionsActions.createScheduledTransaction(
                scheduledTransactionsTypes.mkScheduledTransaction(
                  scheduledTransactionsUtils.mkEstimatedTaxesModel(year, {
                    transaction: {
                      accountId: action.meta?.props.recurrenceAccountId,
                      amount: taxQuarterAmount,
                      postedOn: '',
                    },
                  }),
                ),
              ),
            );
            const { createScheduledTransactionSuccess } = yield race({
              createScheduledTransactionSuccess: take(scheduledTransactionsActions.createScheduledTransactionSuccess),
              createScheduledTransactionFailure: take(scheduledTransactionsActions.createScheduledTransactionFailure),
            });
            if (!createScheduledTransactionSuccess) {
              action.meta?.reject?.({ state: 'SCHEDULED_TRANSACTION_CREATE_ERROR' });
              yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input creation failed')));
              return { state: 'SCHEDULED_TRANSACTION_CREATE_ERROR' };
            }

            // update tax input with stModelId
            yield put(
              actions.updateTaxInput(
                taxesTypes.mkTaxInput({
                  id: taxPlanId,
                  profile: { stModelId: createScheduledTransactionSuccess?.payload?.id },
                }),
              ),
            );
            const { updateTaxInputSuccess } = yield race({
              updateTaxInputSuccess: take(actions.updateTaxInputSuccess),
              updateTaxInputFailure: take(actions.updateTaxInputFailure),
            });
            if (!updateTaxInputSuccess) {
              action.meta?.reject?.({ state: 'TAX_INPUT_UPDATE_ERROR' });
              yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input update failed')));
              return { state: 'TAX_INPUT_UPDATE_ERROR' };
            }
          }

          // complete
          action.meta?.resolve?.({ state: 'COMPLETE' });
          yield put(actions.performTaxPlanRefreshSuccess());
        }
      } catch (error) {
        assert(false, error);
        action.meta?.reject?.({ state: 'TAX_PLAN_REFRESH_ERROR' });
        yield put(actions.performTaxPlanRefreshFailure(new Error('Tax plan refresh failed')));
        return { state: 'TAX_PLAN_REFRESH_ERROR' };
      }
    },
  );
}

export default [
  getTaxCalculationsActionWatcher,
  createTaxInputActionWatcher,
  updateTaxInputActionWatcher,
  deleteTaxInputActionWatcher,
  getTaxInputsActionWatcher,
  createTaxInputFailureActionWatcher,
  performTaxCalcActionWatcher,
  performTaxPlanRefreshActionWatcher,
];
